import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter, useLocation } from 'react-router-dom';
import { SessionProvider } from './app/SessionProvider';
import { AppRoutes } from './router';
import { StreamProvider } from './stream/StreamProvider';
import './styles/tokens.css';
import './styles/app.css';

/**
 * PWA entry (SPEC §10).
 *
 * Provider order matters: the stream sits OUTSIDE the session, because
 * `SessionProvider` reads `credential` / `maintenance` / runner state from the
 * live stream and falls back to `GET /api/auth/me` — stream-first, per
 * CONTRACTS §5.
 */

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: (failureCount, error) => {
        // Never retry a contract error; only transport failures.
        const status = (error as { status?: number }).status;
        if (typeof status === 'number' && status < 500 && status !== 0) return false;
        return failureCount < 2;
      },
      refetchOnWindowFocus: false,
      staleTime: 10_000,
    },
  },
});

function StreamGate(): JSX.Element {
  const location = useLocation();
  // No socket on the login screen: an unauthenticated stream would 401 in a
  // reconnect loop and each attempt burns a rate-limit bucket.
  const onLogin = location.pathname === '/login';

  return (
    <StreamProvider enabled={!onLogin}>
      <SessionProvider>
        <AppRoutes />
      </SessionProvider>
    </StreamProvider>
  );
}

const container = document.getElementById('root');
if (container) {
  createRoot(container).render(
    <StrictMode>
      <QueryClientProvider client={queryClient}>
        <BrowserRouter>
          <StreamGate />
        </BrowserRouter>
      </QueryClientProvider>
    </StrictMode>,
  );
}
